import { useState, useEffect } from 'react'
import type { MachineProfile } from '../types'
import * as db from '../services/db'

export function defaultProfile(): MachineProfile {
  return {
    id: crypto.randomUUID(),
    name: '',
    type: 'milling',
    controller: '',
    travelX: 0,
    travelY: 0,
    travelZ: 0,
    spindlePower: 0,
    maxRpm: 0,
    maxFeedRate: 0,
    safeZ: 0,
    workholding: 'vise',
  }
}

export function useMachineProfiles() {
  const [profiles, setProfiles] = useState<MachineProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    db.fetchMachineProfiles()
      .then(setProfiles)
      .catch(err => setError(String(err?.message ?? err)))
      .finally(() => setLoading(false))
  }, [])

  const addProfile = (profile: MachineProfile) => {
    setProfiles(prev => [...prev, profile])
    db.insertMachineProfile(profile).catch(err => {
      setProfiles(prev => prev.filter(p => p.id !== profile.id))
      setError(String(err?.message ?? err))
    })
  }

  const updateProfile = (id: string, updates: Partial<MachineProfile>) => {
    setProfiles(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p))
    db.updateMachineProfile(id, updates).catch(err => {
      // Re-fetch to restore consistent state on error
      db.fetchMachineProfiles().then(setProfiles).catch(() => undefined)
      setError(String(err?.message ?? err))
    })
  }

  const removeProfile = (id: string) => {
    const snapshot = profiles.find(p => p.id === id)
    setProfiles(prev => prev.filter(p => p.id !== id))
    db.deleteMachineProfile(id).catch(err => {
      // Rollback optimistic delete
      if (snapshot) setProfiles(prev => [...prev, snapshot])
      const code = (err as { code?: string })?.code
      if (code === '23503') {
        setError('Cannot delete: jobs still reference this machine')
      } else {
        setError(String(err?.message ?? err))
      }
    })
  }

  return { profiles, addProfile, updateProfile, removeProfile, loading, error }
}
